// Floating "Update available" pill in the top-right corner.
//
// Two flows in one component because the trigger differs by host:
//
//   1. Browser (web): poll GET /api/version every 60s. If the server's
//      reported version differs from the build-time APP_VERSION baked
//      into this bundle, show the pill. Click → `location.reload()`
//      so Vite/nginx serves the freshly-deployed bundle.
//
//   2. Electron desktop: subscribe to electronAPI.updater.{onAvailable,
//      onDownloaded}. We show the pill the moment electron-updater
//      reports an update, switch the CTA to "Install & restart" once
//      the download finishes, then call updater.installNow() — which
//      flips quitAndInstall and the user lands on the new build.
//
// OSPROD-70 additions:
//   - In-call guard: clicking the pill (or auto-reload firing) while
//     the user is in a LiveKit room shows a confirm and best-effort
//     disconnects the room before reloading, so the call ends cleanly
//     instead of dying on page-unload teardown.
//   - Critical mode: server can flag a release as critical via the
//     APP_VERSION_CRITICAL env var. The pill renders a red countdown
//     variant and auto-reloads at zero, skipping the in-call confirm
//     so stragglers can't pin themselves to a broken build.
//
// Mirrors Discord's behaviour. Quietly self-suppresses when the user
// is on the very first build (no prior version stored), when polling
// fails, or when the document is hidden (no point harassing
// background tabs).

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { Sparkles, RefreshCw, AlertTriangle } from 'lucide-react'
import { APP_VERSION } from '../services/version'
import { IS_ELECTRON, IS_CAPACITOR } from '../services/config'
import api from '../services/api'
import { dialog } from '../services/dialog'
import { useLivekitStore } from '../stores/livekitStore'
import { useIsMobile } from '../hooks/useBreakpoint'

type State =
  | { kind: 'idle' }
  // Web: a different version is up; clicking reloads the page.
  // `critical` flips the visual + auto-reload behavior; `secondsLeft`
  // is only set in critical mode and ticks down to 0.
  | { kind: 'web-update'; serverVersion: string; critical: boolean; secondsLeft?: number }
  // Electron: electron-updater detected a release; downloading.
  | { kind: 'electron-downloading'; targetVersion: string; progressPct: number }
  // Electron: the .exe is on disk waiting for quit-and-install.
  | { kind: 'electron-ready'; targetVersion: string }

const POLL_INTERVAL_MS = 60_000
// Critical updates auto-reload after this many seconds. Long enough
// for the user to wrap up an active call, short enough that they
// can't ignore it.
const CRITICAL_COUNTDOWN_SEC = 60

// Best-effort: end any active LiveKit room before unloading the
// page so the participant list updates immediately for the other
// peers, instead of waiting for the server's heartbeat timeout.
async function teardownCallIfAny() {
  try {
    const room = useLivekitStore.getState().room
    if (room) await useLivekitStore.getState().disconnect()
  } catch { /* unload is happening anyway — don't block */ }
}

// Reload to pick up the freshly-deployed build. In the live-site Electron shell
// a true cache-bypassing reload (== Ctrl+Shift+R) via the preload bridge
// guarantees the new index.html + hashed assets; browsers just reload.
function reloadForUpdate() {
  const api = window.electronAPI
  if (api?.hardReload) api.hardReload()
  else window.location.reload()
}

export default function UpdateAvailableBadge() {
  const [state, setState] = useState<State>({ kind: 'idle' })
  const isMobile = useIsMobile()
  const location = useLocation()
  // On mobile, render the strip into AppLayout's in-flow banner host so it
  // pushes content instead of overlapping the top bar. AppLayout remounts per
  // route, so re-resolve the host on navigation (rAF fallback covers the
  // commit-order gap, mirroring MobileSecondaryPortal).
  const [bannerHost, setBannerHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (!isMobile) { setBannerHost(null); return }
    let raf = 0
    let tries = 0
    const find = () => {
      const h = document.getElementById('rx-mobile-banner-host')
      if (h) setBannerHost(h)
      else if (tries++ < 60) raf = requestAnimationFrame(find)
    }
    raf = requestAnimationFrame(find)
    return () => cancelAnimationFrame(raf)
  }, [isMobile, location.pathname])

  // ---- Electron path ----------------------------------------------------
  useEffect(() => {
    if (!IS_ELECTRON) return
    const updater = (window as any).electronAPI?.updater
    if (!updater) return
    try {
      updater.onAvailable?.((info: any) => {
        setState({
          kind: 'electron-downloading',
          targetVersion: info?.version ?? '?',
          progressPct: 0,
        })
      })
      updater.onProgress?.((p: any) => {
        setState((prev) => {
          if (prev.kind !== 'electron-downloading') return prev
          return { ...prev, progressPct: Math.round(p?.percent ?? 0) }
        })
      })
      updater.onDownloaded?.((info: any) => {
        setState({ kind: 'electron-ready', targetVersion: info?.version ?? '?' })
      })
      // Re-hydrate state the push events already emitted before this mount —
      // e.g. the daily 1am hard reload (or any reload) lands AFTER the
      // download finished, so onDownloaded never fires again for this page.
      // Guard with an explicit if — `getState?.().then` on an older shell
      // (no getState) would throw on the `.then` and the catch below would
      // swallow the check() probe old shells depend on.
      if (typeof updater.getState === 'function') {
        updater.getState()
          .then((s: any) => {
            // Only fill in when still idle: a push event that landed while
            // this promise was in flight carries fresher info (e.g. a newer
            // version finished downloading) — never downgrade it.
            setState((prev) => {
              if (prev.kind !== 'idle') return prev
              if (s?.downloadedVersion) {
                return { kind: 'electron-ready', targetVersion: s.downloadedVersion }
              }
              if (s?.availableVersion) {
                return { kind: 'electron-downloading', targetVersion: s.availableVersion, progressPct: 0 }
              }
              return prev
            })
          })
          .catch(() => {})
      }
      // Initial probe — covers the case where the auto-check fired
      // before this component mounted.
      updater.check?.().catch(() => {})
    } catch { /* non-Electron host or stale preload */ }
  }, [])

  // ---- Web polling path -------------------------------------------------
  // Runs for browsers AND the live-site Electron shell (which loads
  // https://os.rx2go.ai, so "refresh to update" actually pulls the new build).
  // Skipped where a reload can't help: Capacitor mobile (App/Play Store bundle)
  // and any shell still loaded off the baked `file://` bundle.
  useEffect(() => {
    if (IS_CAPACITOR) return
    if (typeof window !== 'undefined' && window.location.protocol === 'file:') return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      if (document.hidden) {
        timer = setTimeout(poll, POLL_INTERVAL_MS)
        return
      }
      try {
        const r = await api.get<{ version: string; critical?: boolean }>('/version')
        const remote = r.data?.version
        const critical = !!r.data?.critical
        if (!cancelled && typeof remote === 'string' && remote && remote !== APP_VERSION && remote !== 'unknown') {
          setState({
            kind: 'web-update',
            serverVersion: remote,
            critical,
            secondsLeft: critical ? CRITICAL_COUNTDOWN_SEC : undefined,
          })
          // Once we know there's an update, stop polling — the version
          // mismatch persists until reload anyway, and a critical
          // countdown shouldn't be racing the next poll tick.
          return
        }
      } catch { /* network glitch — try again next tick */ }
      if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS)
    }
    poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [])

  // ---- Critical-mode countdown -----------------------------------------
  // Drives the visible "N s" countdown decrement and auto-reloads at zero.
  // Re-runs on every state change because the timeout closes over the
  // latest secondsLeft.
  useEffect(() => {
    if (state.kind !== 'web-update' || !state.critical) return
    if (state.secondsLeft === undefined) return
    if (state.secondsLeft <= 0) {
      // Auto-reload. Critical mode skips the in-call confirm by design
      // (see component header comment).
      teardownCallIfAny().finally(() => reloadForUpdate())
      return
    }
    const t = setTimeout(() => {
      setState((prev) => {
        if (prev.kind !== 'web-update' || !prev.critical) return prev
        if (prev.secondsLeft === undefined) return prev
        return { ...prev, secondsLeft: prev.secondsLeft - 1 }
      })
    }, 1000)
    return () => clearTimeout(t)
  }, [state])

  // Never render the update affordance on Capacitor mobile — the bundle is
  // baked into the app, so "refresh to update" is a dead end there.
  if (IS_CAPACITOR) return null
  if (state.kind === 'idle') return null

  const onClick = async () => {
    if (state.kind === 'web-update') {
      const inCall = useLivekitStore.getState().room !== null
      // Non-critical + in call → confirm before yanking the rug.
      // Critical → reload immediately; the countdown is the warning.
      if (inCall && !state.critical) {
        const ok = await dialog.confirm({ message: 'You are in a call right now. Reload the page? The call will end.', tone: 'warning', confirmLabel: 'Reload', cancelLabel: 'Stay' })
        if (!ok) return
      }
      await teardownCallIfAny()
      reloadForUpdate()
      return
    }
    if (state.kind === 'electron-ready') {
      const inCall = useLivekitStore.getState().room !== null
      if (inCall) {
        const ok = await dialog.confirm({ message: 'You are in a call right now. Install the update? The call will end and the app will restart.', tone: 'warning', confirmLabel: 'Install', cancelLabel: 'Stay' })
        if (!ok) return
        await teardownCallIfAny()
      }
      try {
        ;(window as any).electronAPI?.updater?.installNow?.()
      } catch { /* noop */ }
    }
    // electron-downloading — click is a no-op while download progresses.
  }

  const isCritical = state.kind === 'web-update' && state.critical
  const Icon = state.kind === 'electron-downloading'
    ? RefreshCw
    : isCritical
    ? AlertTriangle
    : Sparkles

  const label = (() => {
    if (state.kind === 'web-update') {
      if (state.critical) {
        return `Critical update · ${state.secondsLeft ?? CRITICAL_COUNTDOWN_SEC}s`
      }
      return 'Update available · refresh'
    }
    if (state.kind === 'electron-downloading') {
      return `Downloading update… ${state.progressPct}%`
    }
    return 'Update ready · restart'
  })()

  const tooltip = (() => {
    if (state.kind === 'web-update') {
      if (state.critical) {
        return `The page will refresh automatically in ${state.secondsLeft ?? CRITICAL_COUNTDOWN_SEC}s. Server: ${state.serverVersion}, you: ${APP_VERSION}.`
      }
      return `Server: ${state.serverVersion}, you: ${APP_VERSION}.`
    }
    if (state.kind === 'electron-downloading') {
      return `Downloading ${state.targetVersion}`
    }
    return `Restart to install ${state.targetVersion}`
  })()

  // Critical mode swaps the chip to a red, attention-grabbing variant
  // (with subtle pulse) so the countdown is hard to miss even on a
  // text-heavy screen. Non-critical keeps the existing blurple chip.
  const baseClasses =
    'pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg ring-1 ring-inset transition disabled:opacity-80 disabled:cursor-progress'
  const variantClasses = isCritical
    ? 'bg-red-600 text-white ring-white/30 hover:brightness-110 animate-pulse'
    : 'bg-discord-blurple text-white ring-white/20 hover:brightness-110'

  const badgeButton = (
    <button
      type="button"
      onClick={onClick}
      disabled={state.kind === 'electron-downloading'}
      className={`${baseClasses} ${variantClasses}${isMobile ? ' w-full justify-center' : ''}`}
      title={tooltip}
    >
      <Icon size={13} className={state.kind === 'electron-downloading' ? 'animate-spin' : ''} />
      {label}
    </button>
  )

  // Mobile WITH an AppLayout banner host: portal the in-flow strip so it pushes
  // content instead of overlapping. Mobile WITHOUT a host (narrow window on a
  // route that doesn't mount AppLayout) MUST NOT swallow the banner — fall
  // through to the floating pill. Before this, `<1024px` + no host returned
  // null, so the whole update notice silently vanished for those users
  // (2026-06-09: appeared for wide windows, gone for narrow — a launch-day
  // "I never saw the update prompt" report).
  if (isMobile && bannerHost) {
    return createPortal(
      <div className="w-full shrink-0 px-3 py-1.5 bg-card border-b border-border">{badgeButton}</div>,
      bannerHost,
    )
  }
  // Desktop, or mobile without a host: floating pill in the top-right corner.
  return <div className="fixed top-3 right-3 z-[1100] pointer-events-none">{badgeButton}</div>
}
